import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { errorMessages } from '../utils/messagesUtils';
import { useNavigate } from "react-router-dom";
import { menu } from '../constants';

/**
 * フォーム編集状態の保存・復元を管理するカスタムフック
 * @param {string} storageKeyPrefix - ローカルストレージのキープレフィックス
 * @param {Function} getFormData - フォームデータを取得する関数
 * @param {Function} resetForm - フォームをリセットする関数
 * @param {Object} originalData - サーバーから取得したオリジナルデータ
 */
export const useEditStateStorage = (
  storageKeyPrefix,
  getFormData,
  resetForm,
  originalData,
) => {
  const navigate = useNavigate()
  const [hasChanges, setHasChanges] = useState(false);
  const userId = localStorage.getItem('userId') || 'anonymous';
  const storageKey = `engineer_cloud_${storageKeyPrefix}_${userId}_edit_data`;
  const isSameData = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  
  const AUTO_SAVE_BEFORE_LOGOUT = 3000; // 自動保存の時間（3秒）
  
  // 最新の参照を保持するためのref
  const getFormDataRef = useRef(getFormData);
  const originalDataRef = useRef(originalData);
  
  // 依存値が変わった時にrefを更新
  useEffect(() => {
    getFormDataRef.current = getFormData;
    originalDataRef.current = originalData;

    const currentValues = getFormDataRef.current();
    if (originalData) {
      setHasChanges(!isSameData(currentValues, originalData));
    } else {
      // originalDataがない場合はcurrentValuesに値があれば変更ありとみなす
      setHasChanges(!!currentValues && Object.keys(currentValues).length > 0);
    }
  }, [getFormData, originalData]);

  // 編集内容をローカルストレージに保存する関数
  const saveFormDataToLocalStorage = () => {
    try {
      const currentValues = getFormDataRef.current();
      if (!currentValues) return;
      // データが空かどうかの検証
      if (Array.isArray(currentValues) && currentValues.length === 0) return;
      
      // 実績のPDFファイルは保存しない
      const saveValues = JSON.parse(JSON.stringify(currentValues));
      if (saveValues?.works?.work_pdf) {
        saveValues.works.work_pdf = '';
      }
      
      // 現在ログイン中のユーザーID取得
      if (userId === "anonymous") return;

      // ユーザーIDとデータを含む単一のオブジェクトとして保存
      const dataToStore = {
        userId: userId,
        formData: saveValues
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    } catch (error) {
      // ストレージエラー時は安全のためデータをクリア
      clearStoredData();
    }
  };

  // ローカルストレージのデータをクリア
  const clearStoredData = () => {
    localStorage.removeItem(storageKey);
  };

  // ローカルストレージに保存された編集データをチェック
  const checkForEditData = (serverData) => {
    const savedDataString = localStorage.getItem(storageKey);
    if (!savedDataString) {
      return;
    }

    const savedData = savedDataString ? JSON.parse(savedDataString) : null;

    // データ構造の検証
    if (!savedData || !savedData.userId || !savedData.formData) {
      clearStoredData();
      return;
    }
    
    // 保存データがサーバーデータと同じなら復元不要
    if (isSameData(savedData.formData, serverData)) {
      clearStoredData();
      return;
    }
    
    // 復元確認ダイアログを表示
    Swal.fire({
      title: '編集中の内容があります',
      text: '前回の編集内容を復元しますか？',
      icon: 'question',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ'
    }).then((result) => {
      if (result.isConfirmed) {
        const restoredData = savedData.formData;
        // 実績のPDFファイルの復元を行わない
        if (restoredData?.works?.work_pdf) {
          restoredData.works.work_pdf = '';
        }
        // 編集内容を復元
        resetForm(restoredData);
      }
      // いずれの場合もローカルストレージをクリア
      clearStoredData();
    });
  };

  // login_start_timeとセッション設定を使用した自動ログアウト時の保存処理
  useEffect(() => {
    let logoutTimer = null;
    let checkSessionTimer = null;
    
    const setupAutoLogoutSave = async () => {
      // login_start_timeの取得（なければ現在時刻を設定）
      let loginStartTime = localStorage.getItem('login_start_time');
      if (!loginStartTime) {
        loginStartTime = Date.now().toString();
        localStorage.setItem('login_start_time', loginStartTime);
      }
      loginStartTime = parseInt(loginStartTime, 10);
      
      let sessionDuration = 0;
      
      try {
        // サーバーからセッション設定を取得
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/session-settings`, {
          method: "GET",
          headers: {
            'Accept': "application/json",
            "Content-Type": "application/json",
          },
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (response.status === 200 && data.status === "OK") {
          sessionDuration = data.sessionDuration || 0;
        } else {
          if (response.status === 401) {
            navigate(menu.ENGINEER.SIGNIN.PATH)
          } else {
            const error = new Error();
            error.front_message = errorMessages.common.data_fetch_failed;
            throw error;
          }
        }
      } catch (e) {
        await Swal.fire({ icon: 'error',
          html: e.front_message || errorMessages.api.server_error,
          customClass: {
            htmlContainer: 'large-text'
          }
        });
        return;
      }
      
      // セッション終了時刻の計算
      const sessionEndTime = loginStartTime + sessionDuration;
      const saveBeforeLogoutTime = sessionEndTime - AUTO_SAVE_BEFORE_LOGOUT;
      
      // 現在時刻
      const now = Date.now();
      
      // 保存処理までの残り時間
      const timeUntilSave = Math.max(0, saveBeforeLogoutTime - now);

      // 残り時間がある場合、タイマーを設定
      if (timeUntilSave > 0) {
        logoutTimer = setTimeout(() => {
          // タイマー発火時に最新のデータを取得して保存
          try {
            // 最新のフォームデータを取得して比較
            const currentFormData = getFormDataRef.current();
            const currentOriginalData = originalDataRef.current;
            
            if (currentFormData && currentOriginalData && 
                JSON.stringify(currentFormData) !== JSON.stringify(currentOriginalData)) {
              saveFormDataToLocalStorage();
            }
            
          } finally {
            // ログアウト時の実行
            localStorage.removeItem('userId');
            localStorage.removeItem('login_start_time');
          }
        }, timeUntilSave);
        
        // セッション状態の定期監視
        // 複数タブ間の同期、休止状態からの復帰、ページ再表示時のセッション有効性チェックのため
        checkSessionTimer = setInterval(() => {
          const loginTime = localStorage.getItem('login_start_time');
          // login_start_timeがなくなっていたらセッション切れ
          if (!loginTime) {
            clearInterval(checkSessionTimer);
            return;
          }
          
          // 現在時刻から保存処理までの残り時間を再計算
          const now = Date.now();
          const endTime = parseInt(loginTime, 10) + sessionDuration;
          const saveTime = endTime - AUTO_SAVE_BEFORE_LOGOUT;
          
          // 保存時間を過ぎていたら即時保存
          if (now >= saveTime) {
            clearInterval(checkSessionTimer);
            clearTimeout(logoutTimer);
            
            const currentFormData = getFormDataRef.current();
            const currentOriginalData = originalDataRef.current;
            
            if (currentFormData && currentOriginalData && 
                JSON.stringify(currentFormData) !== JSON.stringify(currentOriginalData)) {
              saveFormDataToLocalStorage();
            }
            
            localStorage.removeItem('userId');
            localStorage.removeItem('login_start_time');
          }
        }, 300000); // 5分ごとにチェック
      } else {
        // 既に保存時間を過ぎている場合は即時処理
        localStorage.removeItem('userId');
        localStorage.removeItem('login_start_time');
      }
    };
    
    setupAutoLogoutSave();
    
    // クリーンアップ関数
    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      if (checkSessionTimer) clearInterval(checkSessionTimer);
    };
  }, []);

  useEffect(() => {
    const handleSaveIfChanged = () => {
      if (hasChanges) {
        saveFormDataToLocalStorage();
      }
    };

    // 監視対象のイベント一覧
    const events = [
      'beforeunload',     // ブラウザ更新時
      'app-logout',       // ログアウト時
      'app-tab-change',   // タブ切替時
      'app-navigation'    // URL変更時
    ];

    // 各イベントにリスナーを登録
    events.forEach(event => window.addEventListener(event, handleSaveIfChanged));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleSaveIfChanged));
    };
  }, [hasChanges]);

  return {
    clearStoredData,
    checkForEditData,
  };
};